/**
 * Finance Summary Cards - Contract Value, Invoiced, Paid, Balance Due
 */
import React from 'react';
import { FileText, Receipt, CreditCard, AlertCircle } from 'lucide-react';
import { formatCurrency } from './utils';

const StatCard = ({ icon: Icon, label, value, subValue, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
  };

  return (
    <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
          <p className="text-xl font-bold mt-0.5">{value}</p>
          {subValue && <p className="text-xs text-slate-400 mt-0.5">{subValue}</p>}
        </div>
      </div>
    </div>
  );
};

export const FinanceSummaryCards = ({ financeSummary, contractValue }) => {
  const totalInvoiced = financeSummary?.total_invoiced || 0;
  const totalPaid = financeSummary?.total_paid || 0;
  const balanceDue = financeSummary?.total_balance_due || 0;
  const invoiceCount = financeSummary?.invoice_count || 0;
  const paymentCount = financeSummary?.payment_count || 0;

  return (
    <div 
      className="grid grid-cols-2 md:grid-cols-4 gap-4"
      data-testid="finance-summary-cards"
    >
      <StatCard
        icon={FileText}
        label="Contract Value"
        value={formatCurrency(contractValue)}
        color="blue"
      />
      <StatCard
        icon={Receipt}
        label="Invoiced"
        value={formatCurrency(totalInvoiced)}
        subValue={`${invoiceCount} invoice${invoiceCount !== 1 ? 's' : ''}`}
        color="blue"
      />
      <StatCard
        icon={CreditCard}
        label="Collected"
        value={formatCurrency(totalPaid)}
        subValue={`${paymentCount} payment${paymentCount !== 1 ? 's' : ''}`}
        color="green"
      />
      <StatCard
        icon={AlertCircle}
        label="Balance Due"
        value={formatCurrency(balanceDue)}
        color={balanceDue > 0 ? 'amber' : 'green'}
      />
    </div>
  );
};

export default FinanceSummaryCards;
